/**
 * reportGrammarIssue — VQE-RO7 user-report writer.
 *
 * Captures a render-anomaly report (buffer + dialect + statementId +
 * diagnostic) and asks the Electron main process to persist it under the
 * user's local data folder. The report **never leaves the user's machine**
 * unless they explicitly share it (a future "Share with Jam team" slice).
 *
 * The function is invoked by `GrammarHarnessReportButton` after the user
 * confirms via the AlertDialog. Pure I/O — does no UI work, accepts an
 * `api` injection seam for tests so the IPC bridge can be stubbed.
 */

import type {
  GrammarHarnessReportReason,
  GrammarHarnessReportRequest,
  GrammarHarnessReportResult,
} from '../../../../types/electron';

export type ReportGrammarIssueContext = {
  reason: GrammarHarnessReportReason;
  bufferText: string;
  dialect: string;
  statementId: string | null;
  diagnostic: string;
  api?: ReportGrammarIssueDeps;
};

export type ReportGrammarIssueDeps = {
  writeReport?: (
    req: GrammarHarnessReportRequest,
  ) => Promise<GrammarHarnessReportResult>;
  nowMs?: () => number;
  randomId?: () => string;
};

const REPORT_ID_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789';

function defaultRandomId(): string {
  let out = '';
  for (let i = 0; i < 8; i += 1) {
    out += REPORT_ID_ALPHABET.charAt(
      Math.floor(Math.random() * REPORT_ID_ALPHABET.length),
    );
  }
  return out;
}

function defaultWriteReport(): NonNullable<ReportGrammarIssueDeps['writeReport']> | null {
  if (typeof window === 'undefined') return null;
  const api = window.electronAPI;
  if (!api?.writeGrammarHarnessReport) return null;
  return (req) => api.writeGrammarHarnessReport(req);
}

export async function reportGrammarIssue(
  ctx: ReportGrammarIssueContext,
): Promise<GrammarHarnessReportResult> {
  if (typeof ctx.bufferText !== 'string') {
    return { success: false, error: 'bufferText is required' };
  }
  if (!ctx.dialect || ctx.dialect.length === 0) {
    return { success: false, error: 'dialect is required' };
  }

  const writer = ctx.api?.writeReport ?? defaultWriteReport();
  if (!writer) {
    return {
      success: false,
      error: 'Grammar-harness reports are not available in this environment',
    };
  }

  const now = ctx.api?.nowMs?.() ?? Date.now();
  const reportId = ctx.api?.randomId?.() ?? defaultRandomId();

  return writer({
    reportId,
    createdAt: now,
    reason: ctx.reason,
    bufferText: ctx.bufferText,
    dialect: ctx.dialect,
    statementId: ctx.statementId,
    diagnostic: ctx.diagnostic,
  });
}
